import React, {
  type ComponentType as ReactComponentType,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import {RowGroup, RowGroupType, NotFoundApiRequestError} from '@common/models'
import {
  useBeforeUnload,
  useParams,
  unstable_useBlocker as useBlocker,
} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {MISSING_ID} from '@common/utils'

import {
  StyledDashboardInnerCard,
  StyledDashboardInnerCardTable,
} from '../../GeneralDataCard.styled'
import {TextAreaDataInput} from './components/TextAreaDataInput'
import {TextDataInput} from './components/TextDataInput'
import {SelectDataInput} from './components/SelectDataInput'
import {ComponentType, ComponentValue, FileRowState} from './components/types'
import {config as cwhtConfig} from './fieldConfig'
import {mojoConfig} from './fieldConfig-mojo'
import {pulsehealthConfig} from './fieldConfig-pulsehealth'
import {PlaceHolder} from './components/PlaceHolder'
import {CalculatedField} from './components/CalculatedField'
import {NamedFileInput} from './components/NamedFileInput'
import {PlanRecommendationInput} from './components/PlanRecommendationInput'
import {UnitConvertibleInput} from './components/UnitConvertibleInput'
import {PreAppointmentSurveyModal} from '../components/PreAppointmentSurveyModal'
import {buildScreeningRowGroups} from './buildScreeningRowGroups'

import {PatientsContext} from '~/components/patients/PatientsContext'
import {Layout} from '~/components/layout/Layout'
import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'
import {
  useGetPatientContactDetails,
  useGetPatientCriticalNote,
  useGetPreAppointmentQuestionnaire,
  useGetPreAppointmentQuestionnaireDefinition,
  useGetScreeningConsultation,
  useUpdateScreeningConsultation,
  useUpdateScreeningConsultationWithFiles,
} from '~/api'
import {Typography} from '~/components/general/typography/Typography'
import {Container} from '~/components/general/container/Container'
import {theme} from '~/styles/theme'
import {Card} from '~/components/general/card/Card'
import {Button} from '~/components/general/button/Button'
import {Loading} from '~/components/general/loading/Loading'
import {useSaveShortcut} from '~/utils'
import {PatientHeader} from '~/components/patients/dashboard/PatientHeader'
import {PractitionerContext} from '~/components/profile/PractitionerContext'

// Component mapping
const componentMap: Record<ComponentType, ReactComponentType<any>> = {
  TextDataInput,
  TextAreaDataInput,
  SelectDataInput,
  PlaceHolder,
  CalculatedField,
  NamedFileInput,
  PlanRecommendationInput,
  UnitConvertibleInput,
}

export function useUnsavedChangesWarning(
  hasChanges: boolean,
  navigationMessage: string,
) {
  // Handle browser unload (refresh/close)
  useBeforeUnload((event) => {
    if (!hasChanges) return
    event.preventDefault()

    event.returnValue = '' // Required to show browser prompt
  })

  const blockerFunction = useCallback(() => {
    if (!hasChanges) return false

    const confirmLeave = window.confirm(navigationMessage)

    return !confirmLeave // Returning true blocks navigation
  }, [hasChanges, navigationMessage])

  // Handle React Router navigation
  useBlocker(blockerFunction)
}

/**
 * Temporary workaround for the Screening Page.
 * This page is used as a temporary solution until a permanent fix is implemented.
 * It displays the screening data for a patient and allows the user to update and save the data.
 */
export const TempScreeningPage = () => {
  const {patient} = useContext(PatientsContext)
  const {selectedEmployment} = useContext(PractitionerContext)
  const {appointmentId} = useParams<{appointmentId: string}>()
  const params = {
    appointmentId: appointmentId ?? '',
    facilityId: selectedEmployment?.facilityId ?? MISSING_ID,
  }
  const {mutate, isPending: isUpdating} = useUpdateScreeningConsultation(params)
  const {mutate: mutateWithFiles, isPending: isUpdatingWithFiles} =
    useUpdateScreeningConsultationWithFiles(params)
  const {addNotification} = useContext(NotificationContext)
  const {t} = useTranslation<string>()
  const [data, setData] = useState<ComponentValue[]>([])
  const [fileRows, setFileRows] = useState<Record<string, FileRowState>>({})
  const [isLoaded, setIsLoaded] = useState(false)
  const [isPreAppointmentModalOpen, setIsPreAppointmentModalOpen] =
    useState(false)
  const preAppointmentParams = {
    facilityId: selectedEmployment?.facilityId ?? MISSING_ID,
    appointmentId: appointmentId ?? MISSING_ID,
  }
  const {
    data: preAppointmentAnswers,
    isInitialLoading: isPreAppointmentAnswersLoading,
    isError: isPreAppointmentAnswersError,
    error: preAppointmentAnswersError,
  } = useGetPreAppointmentQuestionnaire(preAppointmentParams)
  const isPreAppointmentAnswersNotFound =
    isPreAppointmentAnswersError &&
    preAppointmentAnswersError instanceof NotFoundApiRequestError
  const {
    data: preAppointmentDefinition,
    isInitialLoading: isPreAppointmentDefinitionLoading,
  } = useGetPreAppointmentQuestionnaireDefinition(preAppointmentParams, {
    enabled: isPreAppointmentAnswersNotFound,
  })
  const showPreAppointmentButton =
    isPreAppointmentAnswersLoading ||
    !!preAppointmentAnswers ||
    (isPreAppointmentAnswersError && !isPreAppointmentAnswersNotFound) ||
    isPreAppointmentDefinitionLoading ||
    !!preAppointmentDefinition
  const preAppointmentButtonLabel =
    preAppointmentAnswers ||
    (isPreAppointmentAnswersError && !isPreAppointmentAnswersNotFound)
      ? t('View Pre-Appointment Survey')
      : t('Enter Pre-Appointment Survey Data')
  const {isLoading, data: serverData} = useGetScreeningConsultation({
    appointmentId: appointmentId!,
    facilityId: selectedEmployment?.facilityId ?? MISSING_ID,
  })
  const {data: criticalNote} = useGetPatientCriticalNote({
    patientId: patient?.patientId ?? MISSING_ID,
  })
  const {data: contactDetails} = useGetPatientContactDetails({
    patientId: patient?.patientId || MISSING_ID,
  })

  const [hasChanges, setHasChanges] = useState(false)

  /**
   * XXX: TO BE FIXED SOON
   * Determine which configuration to use based on the facility name.
   */
  const facilityName = selectedEmployment?.facilityName?.toLowerCase() ?? ''
  const config = facilityName.includes('pulse health')
    ? pulsehealthConfig
    : facilityName.includes('mojo')
    ? mojoConfig
    : cwhtConfig

  const saveRowGroups = useCallback(() => {
    const {rowGroups, files} = buildScreeningRowGroups(config, data, fileRows)
    if (files.length > 0) {
      mutateWithFiles(
        {rowGroups, files},
        {
          onSuccess: () =>
            addNotification(
              'Data saved successfully',
              NotificationType.Success,
            ),
          onError: () =>
            addNotification('Failed to save data', NotificationType.Error),
        },
      )
    } else {
      mutate(
        {rowGroups},
        {
          onSuccess: () =>
            addNotification(
              'Data saved successfully',
              NotificationType.Success,
            ),
          onError: () =>
            addNotification('Failed to save data', NotificationType.Error),
        },
      )
    }
  }, [config, data, fileRows, mutate, mutateWithFiles, addNotification])

  const handleRemoveFile = useCallback((slotName: string) => {
    setFileRows((prev) => {
      const row = prev[slotName]
      if (!row) return prev
      const next = {...prev}
      next[slotName] = {fileName: row.fileName}
      return next
    })
  }, [])

  const handleFileSelect = useCallback((slotName: string, file: File) => {
    setFileRows((prev) => {
      const row = prev[slotName]
      const next = {...prev}
      next[slotName] = {
        fileName: file.name,
        fileId: row?.fileId,
        pendingFile: file,
      }
      return next
    })
  }, [])

  useUnsavedChangesWarning(
    hasChanges,
    t('You have unsaved changes. Are you sure you want to leave this page?'),
  )
  useSaveShortcut(saveRowGroups)

  const updateConsultationData = async (newData: ComponentValue[]) => {
    const updatedData = newData.reduce(
      (acc: ComponentValue[], newItem: ComponentValue) => {
        const index = acc.findIndex((item) => item.name === newItem.name)
        if (index !== -1) {
          if (newItem.value) {
            acc[index] = newItem
          } else {
            acc.splice(index, 1)
          }
        } else if (newItem.value) {
          acc.push(newItem)
        }
        return acc
      },
      [...data],
    )
    setData(updatedData)
  }

  useEffect(() => {
    if (!isLoading && serverData !== undefined) {
      const fieldRows = serverData.rowGroups.filter(
        (item: RowGroup) => item.type === RowGroupType.FIELD,
      )
      setData(
        fieldRows.map((item: RowGroup) => ({
          name: item.field ?? '',
          value: item.value,
        })) as ComponentValue[],
      )
      const fileRowsFromServer: Record<string, FileRowState> = {}
      const fileRowGroups = serverData.rowGroups.filter(
        (item: RowGroup) => item.type === RowGroupType.FILE,
      )
      const fileSlotNames: string[] = []
      Object.keys(config).forEach((cardTitle) => {
        config[cardTitle].forEach((entry) => {
          if (entry.component === 'NamedFileInput' && entry.name) {
            fileSlotNames.push(entry.name)
          }
        })
      })
      const assignedSlots = new Set<string>()
      fileRowGroups.forEach((item: RowGroup) => {
        const slotByField =
          item.field && fileSlotNames.includes(item.field) ? item.field : null
        const slotByPosition = fileSlotNames.find(
          (name) => !assignedSlots.has(name),
        )
        const slotName = slotByField ?? slotByPosition
        if (slotName) {
          assignedSlots.add(slotName)
          fileRowsFromServer[slotName] = {
            fileName: item.fileName,
            fileId: item.fileId ?? undefined,
          }
        }
      })
      fileSlotNames.forEach((name) => {
        if (!fileRowsFromServer[name]) {
          fileRowsFromServer[name] = {}
        }
      })
      setFileRows(fileRowsFromServer)
      setIsLoaded(true)
    }
  }, [serverData, isLoading, config])

  useEffect(() => {
    if (isLoading || serverData === undefined) {
      return
    }

    const fieldRows = serverData.rowGroups.filter(
      (item: RowGroup) => item.type === RowGroupType.FIELD,
    )
    const originalData = fieldRows.map((item: RowGroup) => ({
      name: item.field ?? '',
      value: item.value,
    }))
    const originalFileRows: Record<string, FileRowState> = {}
    const origFileSlots: string[] = []
    Object.keys(config).forEach((cardTitle) => {
      config[cardTitle].forEach((entry) => {
        if (entry.component === 'NamedFileInput' && entry.name) {
          origFileSlots.push(entry.name)
        }
      })
    })
    const origAssigned = new Set<string>()
    serverData.rowGroups
      .filter((item: RowGroup) => item.type === RowGroupType.FILE)
      .forEach((item: RowGroup) => {
        const slotByField =
          item.field && origFileSlots.includes(item.field) ? item.field : null
        const slotByPosition = origFileSlots.find(
          (name) => !origAssigned.has(name),
        )
        const slotName = slotByField ?? slotByPosition
        if (slotName) {
          origAssigned.add(slotName)
          originalFileRows[slotName] = {
            fileName: item.fileName,
            fileId: item.fileId ?? undefined,
          }
        }
      })
    origFileSlots.forEach((name) => {
      if (!originalFileRows[name]) {
        originalFileRows[name] = {}
      }
    })

    const dataChanged = JSON.stringify(data) !== JSON.stringify(originalData)
    const fileRowsChanged =
      JSON.stringify(fileRows) !== JSON.stringify(originalFileRows)
    setHasChanges(dataChanged || fileRowsChanged)
  }, [data, fileRows, serverData, isLoading, config])

  if (!patient) {
    return (
      <Layout>
        <Typography color="red">Unknown Patient</Typography>
      </Layout>
    )
  }

  return (
    <Layout
      navMenu={
        <Container flex align="center" spacing={1}>
          {showPreAppointmentButton && (
            <Button onClick={() => setIsPreAppointmentModalOpen(true)}>
              {preAppointmentButtonLabel}
            </Button>
          )}
          <Button
            onClick={saveRowGroups}
            disabled={isUpdating || isUpdatingWithFiles}>
            {t('Save Screening Data')}
          </Button>
        </Container>
      }>
      {!isLoaded ? (
        <Loading />
      ) : (
        <>
          <PatientHeader
            patient={patient}
            criticalNote={criticalNote}
            contactDetails={contactDetails}
            patientIdentityEditConfig={{
              facilityId: selectedEmployment?.facilityId ?? MISSING_ID,
            }}
          />
          <br />
          {Object.keys(config).map((cardTitle, index) => (
            <Card key={cardTitle}>
              <Container
                flex
                justify="space-between"
                spacing={2}
                align="center">
                <Typography
                  weight="medium"
                  size="xl"
                  color={theme.colors.secondary.main}>
                  {t(cardTitle)}
                </Typography>

                {index === 0 && (
                  <Typography
                    weight="medium"
                    size="sm"
                    color={theme.colors.other.graphic}>
                    {t('Press Ctrl/⌘ + S to save')}
                  </Typography>
                )}
              </Container>
              <Container direction="row">
                <StyledDashboardInnerCard>
                  <StyledDashboardInnerCardTable>
                    <tbody>
                      {config[cardTitle].reduce((acc, _, i, array) => {
                        if (i % 2 === 0) {
                          const entry1 = array[i]
                          const entry2 = array[i + 1]
                          const Component1 = componentMap[entry1.component]
                          const Component2 = entry2
                            ? componentMap[entry2.component]
                            : null
                          const shouldRenderSecondCell =
                            entry1.component !== 'PlanRecommendationInput'

                          const initialValue1 = data.find(
                            (item) => item.name === entry1.name,
                          )
                          const initialValue2 = entry2
                            ? data.find((item) => item.name === entry2.name)
                            : undefined

                          const cell1 =
                            entry1.component === 'NamedFileInput' ? (
                              <NamedFileInput
                                title={t(entry1.name)}
                                name={entry1.name}
                                fileName={fileRows[entry1.name]?.fileName}
                                fileId={fileRows[entry1.name]?.fileId}
                                pendingFile={fileRows[entry1.name]?.pendingFile}
                                patientId={patient?.patientId ?? MISSING_ID}
                                appointmentId={appointmentId ?? ''}
                                onRemove={() => handleRemoveFile(entry1.name)}
                                onFileSelect={(file) =>
                                  handleFileSelect(entry1.name, file)
                                }
                                currentData={data}
                              />
                            ) : (
                              <Component1
                                title={t(entry1.name)}
                                name={entry1.name}
                                {...(entry1.otherProps ?? {})}
                                onUpdate={updateConsultationData}
                                initialValue={initialValue1?.value}
                                currentData={data}
                              />
                            )

                          const cell2 =
                            !shouldRenderSecondCell ? null : entry2 &&
                              entry2.component === 'NamedFileInput' ? (
                              <NamedFileInput
                                title={t(entry2.name)}
                                name={entry2.name}
                                fileName={fileRows[entry2.name]?.fileName}
                                fileId={fileRows[entry2.name]?.fileId}
                                pendingFile={fileRows[entry2.name]?.pendingFile}
                                patientId={patient?.patientId ?? MISSING_ID}
                                appointmentId={appointmentId ?? ''}
                                onRemove={() => handleRemoveFile(entry2.name)}
                                onFileSelect={(file) =>
                                  handleFileSelect(entry2.name, file)
                                }
                                currentData={data}
                              />
                            ) : Component2 && entry2 ? (
                              <Component2
                                title={t(entry2.name)}
                                name={entry2.name}
                                {...(entry2.otherProps ?? {})}
                                onUpdate={updateConsultationData}
                                initialValue={initialValue2?.value}
                                currentData={data}
                              />
                            ) : null

                          acc.push(
                            <tr key={i}>
                              {cell1}
                              {cell2}
                            </tr>,
                          )
                        }
                        return acc
                      }, [] as JSX.Element[])}
                    </tbody>
                  </StyledDashboardInnerCardTable>
                </StyledDashboardInnerCard>
              </Container>
            </Card>
          ))}
          <Card>
            <Container flex justify="flex-end">
              <Button
                onClick={saveRowGroups}
                disabled={isUpdating || isUpdatingWithFiles}>
                {t('Save Screening Data')}
              </Button>
            </Container>
          </Card>
        </>
      )}
      <PreAppointmentSurveyModal
        isVisible={isPreAppointmentModalOpen}
        onClose={() => setIsPreAppointmentModalOpen(false)}
        facilityId={selectedEmployment?.facilityId ?? MISSING_ID}
        appointmentId={appointmentId ?? ''}
      />
    </Layout>
  )
}
